import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import ReactApexChart from 'react-apexcharts';
import type { ApexAxisChartSeries, ApexOptions } from 'apexcharts';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import {
  Activity,
  Coins,
  Receipt,
  ShoppingBag,
  Ticket,
  TrendingUp,
} from 'lucide-react';
import { graphql } from '@/gql';
import type {
  TicketPresaleByDayQuery,
  TicketPresaleByDayQueryVariables,
  TurnoverAvailableWeeksQuery,
  TurnoverAvailableWeeksQueryVariables,
  TurnoverCompare,
  TurnoverWeekQuery,
  TurnoverWeekQueryVariables,
} from '@/gql/graphql';
import { Card } from '@/components/ui/Card';
import { OverviewPanel } from '@/components/ui/OverviewPanel';
import { Select } from '@/components/ui/Select';
import { EmptyState } from '@/components/ui/EmptyState';
import { QueryErrorNotice } from '@/components/ui/QueryErrorNotice';
import { WeekPicker } from '@/components/ui/DatePickers';
import { seriesColors } from '@/features/charts/chartPalette';
import { escapeChartText } from '@/features/charts/chartText';
import { useSession } from '@/features/session/useSession';
import { useDocumentVisible } from '@/hooks/useDocumentVisible';
import { useUrlState } from '@/hooks/useUrlState';
import { useTheme } from '@/layout/useTheme';
import {
  formatCurrency,
  formatDate,
  formatInteger,
  isoWeek,
  percentChange,
} from '@/lib/format';
import { recognitionDateForPointInTime } from '@/lib/recognitionDate';
import { TURNOVER_COMPARE_OPTIONS, turnoverUrlSchema } from './turnoverUrlState';

const TurnoverDoc = graphql(/* GraphQL */ `
  query TurnoverWeek($year: Int!, $week: Int!, $compare: TurnoverCompare) {
    turnoverWeek(year: $year, week: $week, compare: $compare) {
      year
      week
      startDate
      endDate
      amount
      count
      averageValue
      byDay {
        date
        amount
        count
      }
      byPaymentMethod {
        label
        amount
        count
      }
      byCategory {
        label
        amount
        count
      }
      today {
        date
        amount
        count
        amountSoFar
        countSoFar
        comparisonAmountAtSameTime
        comparisonCountAtSameTime
        pacePercent
        compareMode
        comparisonDay {
          date
          amount
          count
        }
        tickets {
          sold
          scanned
        }
      }
      comparison {
        year
        week
        startDate
        endDate
        amount
        count
        averageValue
        byDay {
          date
          amount
          count
        }
      }
    }
  }
`) as TypedDocumentNode<TurnoverWeekQuery, TurnoverWeekQueryVariables>;
const TurnoverAvailableWeeksDoc = graphql(/* GraphQL */ `
  query TurnoverAvailableWeeks($year: Int!) {
    turnoverAvailableWeeks(year: $year)
  }
`) as TypedDocumentNode<
  TurnoverAvailableWeeksQuery,
  TurnoverAvailableWeeksQueryVariables
>;

const TicketPresaleByDayDoc = graphql(/* GraphQL */ `
  query TicketPresaleByDay {
    ticketPresaleByDay {
      categories
      series {
        nightId
        name
        data
      }
    }
  }
`) as TypedDocumentNode<
  TicketPresaleByDayQuery,
  TicketPresaleByDayQueryVariables
>;

export function TurnoverPage() {
  const { me } = useSession();
  const { resolved: theme } = useTheme();
  const visible = useDocumentVisible();
  const [now, setNow] = useState(() => new Date());
  // Re-evaluate the clock every minute so a week/recognition-day rollover moves
  // the report. Paused while the tab is hidden (a rollover would refetch);
  // catches up as soon as the tab is visible again.
  useEffect(() => {
    if (!visible) return;
    const tick = () => {
      setNow((prev) => {
        const next = new Date();
        const prevWeek = isoWeek(prev);
        const nextWeek = isoWeek(next);
        const weekChanged =
          prevWeek.year !== nextWeek.year || prevWeek.week !== nextWeek.week;
        const dayChanged =
          recognitionDateForPointInTime(prev) !==
          recognitionDateForPointInTime(next);
        return weekChanged || dayChanged ? next : prev;
      });
    };
    tick();
    const id = window.setInterval(tick, 60_000);
    return () => window.clearInterval(id);
  }, [visible]);
  const current = useMemo(() => isoWeek(now), [now]);
  const isCurrentWeekOnly =
    me?.permissions?.includes('report:turnover:current_week:read') === true &&
    me?.permissions?.includes('report:turnover:read') !== true;
  // Without `year` and `week` in the URL the page follows the latest week that has data; picking that week
  // again (see `selectWeek`) goes back to following it.
  const { state: url, patch } = useUrlState(turnoverUrlSchema);
  const followsLatestWeek = url.year === 0 || url.week === 0;
  const compare = url.compare;

  const recognitionDate = useMemo(
    () => recognitionDateForPointInTime(now),
    [now],
  );
  const queryCompare: TurnoverCompare = isCurrentWeekOnly
    ? 'LAST_WEEK'
    : compare;
  const todayCompareSource: TurnoverCompare = isCurrentWeekOnly
    ? 'LAST_WEEK'
    : compare;

  const weeks0 = useQuery(TurnoverAvailableWeeksDoc, {
    variables: { year: current.year },
  });
  const weeks1 = useQuery(TurnoverAvailableWeeksDoc, {
    variables: { year: current.year - 1 },
    skip: isCurrentWeekOnly,
  });
  const weeks2 = useQuery(TurnoverAvailableWeeksDoc, {
    variables: { year: current.year - 2 },
    skip: isCurrentWeekOnly,
  });
  const weeks3 = useQuery(TurnoverAvailableWeeksDoc, {
    variables: { year: current.year - 3 },
    skip: isCurrentWeekOnly,
  });
  const weeks4 = useQuery(TurnoverAvailableWeeksDoc, {
    variables: { year: current.year - 4 },
    skip: isCurrentWeekOnly,
  });
  const weeks5 = useQuery(TurnoverAvailableWeeksDoc, {
    variables: { year: current.year - 5 },
    skip: isCurrentWeekOnly,
  });
  const weekQueries = [
    { year: current.year, query: weeks0 },
    { year: current.year - 1, query: weeks1 },
    { year: current.year - 2, query: weeks2 },
    { year: current.year - 3, query: weeks3 },
    { year: current.year - 4, query: weeks4 },
    { year: current.year - 5, query: weeks5 },
  ];
  let latestAvailable: { year: number; week: number } | null = null;
  let latestAvailabilityError;
  for (const candidate of weekQueries) {
    if (candidate.query.error) {
      latestAvailabilityError = candidate.query.error;
      break;
    }
    const weeks = candidate.query.data?.turnoverAvailableWeeks;
    if (!weeks) break;
    const latestWeek = weeks[weeks.length - 1];
    if (latestWeek !== undefined) {
      latestAvailable = { year: candidate.year, week: latestWeek };
      break;
    }
  }
  const year = isCurrentWeekOnly
    ? current.year
    : followsLatestWeek
      ? (latestAvailable?.year ?? current.year)
      : url.year;
  const selectedWeeksQuery =
    weekQueries.find((candidate) => candidate.year === year)?.query ?? weeks0;
  const weeksView =
    selectedWeeksQuery.data ?? selectedWeeksQuery.previousData;
  const availableWeeks = useMemo(
    () => weeksView?.turnoverAvailableWeeks ?? [],
    [weeksView],
  );
  const weeksLoading = selectedWeeksQuery.loading;
  const weeksError = selectedWeeksQuery.error;
  const availabilityError = isCurrentWeekOnly
    ? undefined
    : followsLatestWeek
      ? latestAvailabilityError
      : weeksError;

  let week = url.week; // 0 while following; both branches that apply then overwrite it
  if (isCurrentWeekOnly) {
    week = current.week;
  } else if (followsLatestWeek) {
    week = latestAvailable?.week ?? current.week;
  } else if (
    availableWeeks.length > 0 &&
    !availableWeeks.includes(url.week)
  ) {
    week = availableWeeks[availableWeeks.length - 1] ?? url.week;
  }
  const selectWeek = (next: { year: number; week: number }) => {
    const latest = latestAvailable;
    const followsAgain = latest !== null && next.year === latest.year && next.week === latest.week;
    patch(followsAgain ? { year: 0, week: 0 } : next);
  };
  const isViewingCurrentWeek =
    year === current.year && week === current.week;

  const { data, previousData, loading } = useQuery(TurnoverDoc, {
    variables: { year, week, compare: queryCompare },
    pollInterval: isViewingCurrentWeek && visible ? 60_000 : 0,
  });

  // Presale chart: not tied to the selected week. A failure (e.g. no
  // `report:turnover:read`) is shown inside its own card only.
  const {
    data: presaleData,
    previousData: presalePreviousData,
    loading: presaleLoading,
    error: presaleError,
  } = useQuery(TicketPresaleByDayDoc, { pollInterval: visible ? 60_000 : 0 });
  const presale = (presaleData ?? presalePreviousData)?.ticketPresaleByDay;

  const payload = (data ?? previousData)?.turnoverWeek;
  const weekComparison =
    !isCurrentWeekOnly && compare !== 'NONE'
      ? (payload?.comparison ?? null)
      : null;
  const amount = payload?.amount ?? 0;
  const count = payload?.count ?? 0;
  const averageValue = payload?.averageValue ?? 0;
  const byDay = useMemo(() => payload?.byDay ?? [], [payload]);
  const amountDelta = weekComparison
    ? percentChange(amount, weekComparison.amount ?? 0)
    : null;
  const countDelta = weekComparison
    ? percentChange(count, weekComparison.count ?? 0)
    : null;
  const avgDelta = weekComparison
    ? percentChange(averageValue, weekComparison.averageValue ?? 0)
    : null;

  const todayData = payload?.today ?? null;
  const todayHasComparison = todayCompareSource !== 'NONE';
  const todayComparisonDay = todayHasComparison
    ? (todayData?.comparisonDay ?? null)
    : null;
  const todayCompareLabel = todayCompareModeLabel(todayCompareSource);
  const todayAmount = todayData?.amount ?? 0;
  const todayCompareAmount = todayComparisonDay?.amount ?? 0;
  const todayAmountDelta = todayComparisonDay
    ? percentChange(todayAmount, todayCompareAmount)
    : null;
  const todayCount = todayData?.count ?? 0;
  const todayCompareCount = todayComparisonDay?.count ?? 0;
  const todayCountDelta = todayComparisonDay
    ? percentChange(todayCount, todayCompareCount)
    : null;
  const todayAverage = todayCount > 0 ? todayAmount / todayCount : 0;
  const todayCompareAverage =
    todayCompareCount > 0 ? todayCompareAmount / todayCompareCount : 0;
  const todayAvgDelta = todayComparisonDay
    ? percentChange(todayAverage, todayCompareAverage)
    : null;
  const todayPacePercent = todayHasComparison
    ? (todayData?.pacePercent ?? null)
    : null;
  const todayPanelTitle = todayHasComparison
    ? `Today · ${formatDate(todayData?.date ?? recognitionDate)} · vs ${todayCompareLabel}`
    : `Today · ${formatDate(todayData?.date ?? recognitionDate)}`;
  // Null when no ticketed night starts today or none has a sold ticket.
  const todayTickets = todayData?.tickets ?? null;
  // A ticket night with a EUR 0 bar still shows the Today panel.
  const showTodayBar =
    isViewingCurrentWeek &&
    ((todayData?.amount ?? 0) > 0 || todayTickets !== null);

  const dailyChart = useMemo<ApexOptions>(() => {
    const categories = byDay.map((d) => formatDate(d.date ?? ''));
    const metricSeries = byDay.map((d) => Number((d.amount ?? 0).toFixed(2)));
    const compareSeries =
      weekComparison?.byDay?.map((d) => Number((d.amount ?? 0).toFixed(2))) ??
      [];

    return {
      chart: {
        type: 'bar',
        toolbar: { show: false },
        fontFamily: 'inherit',
        height: 320,
      },
      plotOptions: { bar: { borderRadius: 6, columnWidth: '55%' } },
      colors: ['#fc0090', '#9ca3af'],
      xaxis: { categories },
      yaxis: {
        min: 0,
        forceNiceScale: true,
        labels: { formatter: (v: number) => formatCurrency(v) },
      },
      stroke: { width: [0, 2], curve: 'smooth' },
      tooltip: {
        theme: theme === 'dark' ? 'dark' : 'light',
        y: { formatter: (v: number) => formatCurrency(v) },
      },
      legend: { position: 'top' },
      dataLabels: { enabled: false },
      series:
        weekComparison !== null && compareSeries.length > 0
          ? [
              {
                name: `Week ${payload?.week}`,
                type: 'column',
                data: metricSeries,
              },
              {
                name:
                  queryCompare === 'LAST_WEEK'
                    ? 'Previous week'
                    : 'Same week last year',
                type: 'line',
                data: compareSeries,
              },
            ]
          : [
              {
                name: `Week ${payload?.week}`,
                type: 'column',
                data: metricSeries,
              },
            ],
    };
  }, [payload, weekComparison, queryCompare, byDay, theme]);

  const presaleChart = useMemo(
    () =>
      presaleCumulativeOptions(
        presale?.categories ?? [],
        presale?.series ?? [],
        theme === 'dark' ? 'dark' : 'light',
      ),
    [presale, theme],
  );

  const yearOptions = useMemo(
    () =>
      Array.from({ length: 6 }, (_, i) => current.year - i).map((y) => ({
        value: String(y),
        label: String(y),
      })),
    [current.year],
  );
  const hasWeeks = availableWeeks.length > 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Turnover
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {payload
              ? `Week ${payload.week} · ${formatDate(payload.startDate)} – ${formatDate(payload.endDate)}`
              : 'Loading week…'}
          </p>
        </div>
        {!isCurrentWeekOnly ? (
          <div className="flex flex-wrap gap-2">
            <Select
              value={String(year)}
              onChange={(event) => selectWeek({ year: Number(event.target.value), week })}
              options={yearOptions}
              aria-label="Year"
            />
            <div className="min-w-[12rem]">
              <WeekPicker
                value={{ year, week }}
                onChange={selectWeek}
                availableWeeks={availableWeeks}
                disabled={
                  !hasWeeks ||
                  (weeksLoading && !weeksView) ||
                  weeksError !== undefined
                }
                ariaLabel="Week"
              />
            </div>
            <Select
              value={compare}
              onChange={(event) =>
                patch({ compare: event.target.value as TurnoverCompare })
              }
              options={TURNOVER_COMPARE_OPTIONS}
              aria-label="Compare mode"
            />
          </div>
        ) : null}
      </div>

      {availabilityError ? (
        <QueryErrorNotice
          error={availabilityError}
          title="Could not determine the latest turnover week"
        />
      ) : null}

      {showTodayBar ? (
        <OverviewPanel
          title={todayPanelTitle}
          stats={[
            {
              id: 'today-amount',
              label: "Today's amount",
              value: formatCurrency(todayAmount),
              icon: <Coins className="h-5 w-5" />,
              delta:
                todayAmountDelta !== null
                  ? {
                      value: todayAmountDelta,
                      direction: todayAmountDelta >= 0 ? 'up' : 'down',
                    }
                  : undefined,
              hint: todayComparisonDay
                ? `vs ${formatCurrency(todayCompareAmount)} (${todayCompareLabel})`
                : undefined,
            },
            {
              id: 'today-count',
              label: 'Transactions today',
              value: formatInteger(todayCount),
              icon: <Receipt className="h-5 w-5" />,
              delta:
                todayCountDelta !== null
                  ? {
                      value: todayCountDelta,
                      direction: todayCountDelta >= 0 ? 'up' : 'down',
                    }
                  : undefined,
              hint: todayComparisonDay
                ? `vs ${formatInteger(todayCompareCount)} (${todayCompareLabel})`
                : undefined,
            },
            {
              id: 'today-avg',
              label: "Today's average",
              value: formatCurrency(todayAverage),
              icon: <TrendingUp className="h-5 w-5" />,
              delta:
                todayAvgDelta !== null
                  ? {
                      value: todayAvgDelta,
                      direction: todayAvgDelta >= 0 ? 'up' : 'down',
                    }
                  : undefined,
              hint: todayComparisonDay
                ? `vs ${formatCurrency(todayCompareAverage)} (${todayCompareLabel})`
                : undefined,
            },
            {
              id: 'today-pace',
              label: todayHasComparison
                ? `On track · ${todayCompareLabel}`
                : 'So far today',
              value:
                todayHasComparison && todayPacePercent !== null
                  ? `${todayPacePercent.toFixed(0)}%`
                  : formatCurrency(todayData?.amountSoFar ?? 0),
              icon: <Activity className="h-5 w-5" />,
              delta:
                todayHasComparison && todayPacePercent !== null
                  ? {
                      value: todayPacePercent - 100,
                      label: todayPacePercent >= 100 ? 'ahead' : 'behind',
                      direction: todayPacePercent >= 100 ? 'up' : 'down',
                    }
                  : undefined,
              hint: todayHasComparison
                ? `${formatCurrency(todayData?.amountSoFar ?? 0)} so far · comparison had ${formatCurrency(todayData?.comparisonAmountAtSameTime ?? 0)} at this time`
                : 'Ledger turnover so far today',
            },
            ...(todayTickets
              ? [
                  {
                    id: 'admin.turnover.today.tickets',
                    label: 'Tickets scanned',
                    value: `${formatInteger(todayTickets.scanned)} / ${formatInteger(todayTickets.sold)}`,
                    icon: <Ticket className="h-5 w-5" />,
                    hint: `${Math.round((todayTickets.scanned / todayTickets.sold) * 100)}% showed up`,
                  },
                ]
              : []),
          ]}
        />
      ) : null}

      <OverviewPanel
        title={showTodayBar ? 'This week' : undefined}
        stats={[
          {
            id: 'amount',
            label: 'Amount',
            value: formatCurrency(amount),
            icon: <Coins className="h-5 w-5" />,
            delta:
              amountDelta !== null
                ? {
                    value: amountDelta,
                    direction: amountDelta >= 0 ? 'up' : 'down',
                  }
                : undefined,
            hint: weekComparison
              ? `vs ${formatCurrency(weekComparison.amount ?? 0)}`
              : undefined,
          },
          {
            id: 'count',
            label: 'Transactions',
            value: formatInteger(count),
            icon: <Receipt className="h-5 w-5" />,
            delta:
              countDelta !== null
                ? {
                    value: countDelta,
                    direction: countDelta >= 0 ? 'up' : 'down',
                  }
                : undefined,
            hint: weekComparison
              ? `vs ${formatInteger(weekComparison.count ?? 0)}`
              : undefined,
          },
          {
            id: 'avg',
            label: 'Average transaction value',
            value: formatCurrency(averageValue),
            icon: <TrendingUp className="h-5 w-5" />,
            delta:
              avgDelta !== null
                ? { value: avgDelta, direction: avgDelta >= 0 ? 'up' : 'down' }
                : undefined,
            hint: weekComparison
              ? `vs ${formatCurrency(weekComparison.averageValue ?? 0)}`
              : undefined,
          },
          {
            id: 'range',
            label: 'Days with sales',
            value: formatInteger(
              byDay.filter((d) => (d.amount ?? 0) > 0).length,
            ),
            icon: <ShoppingBag className="h-5 w-5" />,
          },
        ]}
      />

      <Card title="Amount by day">
        {loading && !payload ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
        ) : byDay.length === 0 ? (
          <EmptyState
            title="No sales recorded"
            description="This week has no ledger turnover yet."
          />
        ) : (
          <ReactApexChart
            options={dailyChart}
            series={dailyChart.series ?? []}
            type="bar"
            height={320}
          />
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="By payment method">
          <BreakdownTable rows={normalizeSlices(payload?.byPaymentMethod)} />
        </Card>
        <Card title="By category">
          <BreakdownTable rows={normalizeSlices(payload?.byCategory)} />
        </Card>
      </div>

      <Card id="admin.turnover.ticketsSold" title="Tickets sold by day">
        {presaleError && !presale ? (
          <div className="p-5">
            <QueryErrorNotice
              error={presaleError}
              title="Could not load ticket sales"
            />
          </div>
        ) : presaleLoading && !presale ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
        ) : !presale || presale.series.length === 0 ? (
          <EmptyState
            title="No presale tickets sold"
            description="Online and member tickets for upcoming nights show up here, per recognition day."
          />
        ) : (
          <ReactApexChart
            options={presaleChart}
            series={presaleChart.series ?? []}
            type="bar"
            height={320}
          />
        )}
      </Card>
    </div>
  );
}

type PresaleRow = {
  nightId: unknown;
  name: string;
  data: readonly number[];
};

type PresaleDay = { sold: number; cumulative: number };

/**
 * Each night is one side-by-side stack (`series.group`): a lighter prior
 * cumulative total under that day's sales. The two segments cannot share a
 * series name — Apex finds a segment's place in the group with indexOf(name).
 * A zero segment stays 0, not null, so the other segment still sits on the
 * baseline; bar charts draw no fill for 0.
 */
function presaleCumulativeOptions(
  categories: readonly string[],
  rows: readonly PresaleRow[],
  tooltipTheme: 'light' | 'dark',
): ApexOptions {
  const labels = categories.map((day) => formatDate(day));
  const palette = seriesColors(rows.length);
  const colors: string[] = [];
  const legend: string[] = [];
  const legendColors: string[] = [];
  const series: ApexAxisChartSeries = [];
  const daysByNight: PresaleDay[][] = [];
  const owner: number[] = [];

  rows.forEach((row, index) => {
    const color = palette[index] ?? '#fc0090';
    const title = escapeChartText(row.name);
    const group = String(row.nightId);
    let running = 0;
    const prior: number[] = [];
    const soldOnDay: number[] = [];
    const days: PresaleDay[] = [];
    for (const raw of row.data) {
      const sold = raw ?? 0;
      prior.push(running);
      soldOnDay.push(sold);
      running += sold;
      days.push({ sold, cumulative: running });
    }
    series.push(
      { name: `${title}\u2060${group}`, group, data: prior },
      { name: `${title}\u2060${group}\u2060`, group, data: soldOnDay },
    );
    daysByNight.push(days);
    owner.push(index, index);
    colors.push(lighterTint(color), color);
    legend.push(title);
    legendColors.push(color);
  });

  return {
    chart: {
      type: 'bar',
      stacked: true,
      toolbar: { show: false },
      fontFamily: 'inherit',
      animations: { enabled: false },
      height: 320,
    },
    plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
    stroke: { width: 0 },
    states: {
      hover: { filter: { type: 'none' } },
      active: { filter: { type: 'none' } },
    },
    colors,
    xaxis: {
      categories: labels,
      labels: { hideOverlappingLabels: false },
    },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      decimalsInFloat: 0,
      labels: { formatter: (value: number) => String(Math.round(value)) },
    },
    legend: {
      position: 'top',
      showForSingleSeries: true,
      customLegendItems: legend,
      clusterGroupedSeries: false,
      markers: { fillColors: legendColors },
    },
    dataLabels: { enabled: false },
    tooltip: {
      shared: false,
      intersect: true,
      theme: tooltipTheme,
      custom: ({ seriesIndex, dataPointIndex }) => {
        const nightIndex = owner[seriesIndex as number];
        const point =
          daysByNight[nightIndex]?.[dataPointIndex as number];
        if (!point || nightIndex === undefined) return '';
        return presaleTooltipHtml({
          date: labels[dataPointIndex as number] ?? '',
          name: legend[nightIndex] ?? '',
          color: legendColors[nightIndex] ?? '#fc0090',
          sold: point.sold,
          cumulative: point.cumulative,
        });
      },
    },
    series,
  };
}

function presaleTooltipHtml({
  date,
  name,
  color,
  sold,
  cumulative,
}: {
  date: string;
  name: string;
  color: string;
  sold: number;
  cumulative: number;
}): string {
  const swatch = /^#[0-9a-fA-F]{6}$/.test(color) ? color : '#fc0090';
  return [
    `<div class="apexcharts-tooltip-title">${escapeChartText(date)}</div>`,
    '<div class="apexcharts-tooltip-series-group apexcharts-active" style="display:flex">',
    `<span class="apexcharts-tooltip-marker" shape="circle" style="color:${swatch}"></span>`,
    '<div class="apexcharts-tooltip-text">',
    `<div class="apexcharts-tooltip-y-group"><span class="apexcharts-tooltip-text-y-label">${name}</span></div>`,
    `<div class="apexcharts-tooltip-y-group"><span class="apexcharts-tooltip-text-y-label">Sold this day:</span><span class="apexcharts-tooltip-text-y-value">${sold}</span></div>`,
    `<div class="apexcharts-tooltip-y-group"><span class="apexcharts-tooltip-text-y-label">Cumulative:</span><span class="apexcharts-tooltip-text-y-value">${cumulative}</span></div>`,
    '</div></div>',
  ].join('');
}

/** Mix a #rrggbb colour toward white so the prior segment stays the same hue. */
function lighterTint(hex: string, towardWhite = 0.5): string {
  const match = /^#([0-9a-fA-F]{6})$/.exec(hex);
  if (!match) return hex;
  const raw = match[1];
  const channel = (offset: number) =>
    Number.parseInt(raw.slice(offset, offset + 2), 16);
  const mix = (value: number) =>
    Math.round(value + (255 - value) * towardWhite)
      .toString(16)
      .padStart(2, '0');
  return `#${mix(channel(0))}${mix(channel(2))}${mix(channel(4))}`;
}

function todayCompareModeLabel(compareMode: TurnoverCompare): string {
  if (compareMode === 'SAME_WEEK_LAST_YEAR') return 'same day last year';
  if (compareMode === 'LAST_WEEK') return 'previous week';
  return '';
}

function normalizeSlices(
  rows:
    | ReadonlyArray<{
        label?: string | null;
        amount?: number | null;
        count?: number | null;
      }>
    | undefined
    | null,
): Array<{ label: string; amount: number; count: number }> {
  return (rows ?? []).map((r) => ({
    label: r.label ?? '',
    amount: r.amount ?? 0,
    count: r.count ?? 0,
  }));
}

function BreakdownTable({
  rows,
}: {
  rows: ReadonlyArray<{ label: string; amount: number; count: number }>;
}) {
  if (rows.length === 0) {
    return (
      <EmptyState
        title="Nothing to show"
        description="No transactions matched this breakdown for the selected week."
      />
    );
  }
  return (
    <table className="w-full text-left text-sm">
      <thead className="text-xs tracking-wide text-gray-500 uppercase">
        <tr>
          <th className="px-2 py-2">Label</th>
          <th className="px-2 py-2 text-right">Amount</th>
          <th className="px-2 py-2 text-right">Count</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 dark:divide-white/10">
        {rows.map((row) => (
          <tr key={row.label}>
            <td className="px-2 py-2 font-medium text-gray-700 dark:text-gray-200">
              {row.label}
            </td>
            <td className="px-2 py-2 text-right">
              {formatCurrency(row.amount)}
            </td>
            <td className="px-2 py-2 text-right">{formatInteger(row.count)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
